/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { PasswordField } from "../components/FormUtils";
import { MyActionButton } from "../components/ActionButtonUtils";
import { getOneAdmin } from "../../lib/sysNavigation/SysNavigation";
import { validatePassword } from "../../lib/validators";
import { useAdminsContext } from "../../context/admin/AdminManageContext";

const PasswordSettings = ({
  loading,
  setError,
  password,
  setAction,
  setSuccess,
  setPassword,
  setErrorMessage,
  confirm_password,
  setConfirmPassword,
  setLoading,
  responses,
}) => {
  const { updatePassword } = useAdminsContext();
  const admin = getOneAdmin();
  const admin_id = admin?.admin_id;

  const updatePasswordHandler = async (e?: any) => {
    e?.preventDefault?.();
    setLoading(true);
    const { isError, message } = validatePassword(password);
    if (isError) {
      responses(true, message);
    } else if (password !== confirm_password) {
      responses(true, "Passwords Should Match");
    } else {
      await sendPasswordRequest();
    }
  };

  const sendPasswordRequest = async () => {
    const data = { password: password };
    const { message, status } = await updatePassword(admin_id, data);
    if (status === 200 || status === 201) {
      setPassword(null);
      setConfirmPassword(null);
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  // Visual-only strength + requirement checks
  const meets = {
    length: (password || "").length >= 8,
    upper: /[A-Z]/.test(password || ""),
    number: /\d/.test(password || ""),
    symbol: /[^A-Za-z0-9]/.test(password || ""),
  };
  const metCount = Object.values(meets).filter(Boolean).length;

  const strength = (() => {
    if (!password) return { label: "Start typing…", width: "w-0", barOpacity: "opacity-0" };
    if (metCount <= 1) return { label: "Very weak", width: "w-1/4", barOpacity: "opacity-40" };
    if (metCount === 2) return { label: "Weak", width: "w-1/2", barOpacity: "opacity-60" };
    if (metCount === 3) return { label: "Good", width: "w-3/4", barOpacity: "opacity-80" };
    return { label: "Strong", width: "w-full", barOpacity: "opacity-100" };
  })();

  const confirmMatch =
    (password || "").length > 0 &&
    (confirm_password || "").length > 0 &&
    password === confirm_password;

  return (
    <div
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          updatePasswordHandler(e);
        }
      }}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PasswordField
          value={password}
          required={true}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(null);
            setSuccess(null);
          }}
        />
        <PasswordField
          value={confirm_password}
          isConfirmPassword={true}
          required={true}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setError(null);
            setSuccess(null);
          }}
        />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px]">
          <span className="font-semibold text-gray-700">Strength</span>
          <span className="font-semibold text-gray-700" aria-live="polite">
            {strength.label}
          </span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
          <div
            className={[
              "h-2 rounded-full bg-brand transition-all duration-300",
              strength.width,
              strength.barOpacity,
            ].join(" ")}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <RequirementPill ok={meets.length} label="8+ characters" />
        <RequirementPill ok={meets.upper} label="Uppercase" />
        <RequirementPill ok={meets.number} label="Number" />
        <RequirementPill ok={meets.symbol} label="Symbol" />
        <MatchPill ok={confirmMatch} hasInput={!!(password || confirm_password)} />
      </div>

      <div className="mt-5 flex items-center justify-end border-t border-gray-100 pt-4">
        <MyActionButton
          type="button"
          loading={loading}
          title="Update Password"
          onClick={() => {
            setError(false);
            setSuccess(false);
            setAction("password");
            setErrorMessage(null);
            updatePasswordHandler();
          }}
        />
      </div>
    </div>
  );
};

export default PasswordSettings;

const RequirementPill = ({ ok, label }) => {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset",
        ok
          ? "bg-brand/10 text-brand ring-brand/30"
          : "bg-gray-50 text-gray-500 ring-gray-200",
      ].join(" ")}
      aria-checked={ok}
      role="checkbox"
    >
      <Dot ok={ok} />
      {label}
    </span>
  );
};

const MatchPill = ({ ok, hasInput }: any) => {
  const label = ok ? "Passwords match" : hasInput ? "Awaiting match" : "Start typing…";
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset",
        ok
          ? "bg-brand/10 text-brand ring-brand/30"
          : "bg-gray-50 text-gray-500 ring-gray-200",
      ].join(" ")}
      aria-live="polite"
    >
      <Dot ok={ok} />
      {label}
    </span>
  );
};

const Dot = ({ ok }: any) => {
  return (
    <span
      className={[
        "inline-block h-1.5 w-1.5 rounded-full",
        ok ? "bg-brand" : "bg-gray-300",
      ].join(" ")}
      aria-hidden="true"
    />
  );
};
